import { useEffect, useState } from "react";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { buttonUiTemplate } from "../frontViews/buttonUiTemplate";
import type { C1_Button } from "../model/C1_Button";
import { OrientedButtonFrontView, type ButtonFrontViewProps } from "../frontViews/ButtonFrontview";
import { getC1ButtonDevice, getC1ButtonRows } from "../frontViews/C1_buttonRows";
import c1_state from "../model/C1_State";

type C1_ButtonViewProps = Omit<ButtonFrontViewProps, "rows" | "uiTemplate" | "orientation"> & {
  eloItem: C1_Button;
  frontImageReflected?: boolean;
};

/** Stellt einen EloItem-Taster mit seinem konfigurierten Produkt und seinen Funktionen dar. */
export function C1_ButtonView({ eloItem, frontImageReflected = false, ...props }: C1_ButtonViewProps) {
  const device = getC1ButtonDevice(eloItem);
  useOnChangeWatcher(device);
  useOnChangeWatcher(device?.template);
  useOnChangeWatcher(device?.channels);
  useOnChangeWatcher(c1_state.editMode);
  const buttonBaseProps = device?.baseProps?.buttonBaseProps;
  const template = device?.template;
  const renderingPdf = c1_state.floorImageRenderOptions.value !== null;
  const standaloneElectrical = state.settings.applicationMode === "c1"
    && ["EloConcept", "EloDetail"].includes(c1_state.editMode.value);
  const useFrontImage = !!device && (renderingPdf || (!!template && (standaloneElectrical
    || (template.baseProps === undefined && template.genericParams === undefined))));
  const imageSource = () => useFrontImage
    ? (renderingPdf ? device?.frontImage : standaloneElectrical ? template?.knxProductData.frontImage ?? device?.frontImage : device?.frontImage)
    : undefined;
  const [frontImage, setFrontImage] = useState<string | undefined>(imageSource);
  useEffect(() => {
    let active = true;
    setFrontImage(imageSource());
    if (useFrontImage && template) {
      void template.knxProductData.loadFrontImage().then(() => {
        if (active) setFrontImage(imageSource());
      }).catch(() => { /* Keep the image area empty when no product image is available. */ });
    }
    return () => { active = false; };
  }, [device, template, useFrontImage, standaloneElectrical, renderingPdf, template?.useGenericImage]);

  const { x = 0, y = 0, width = 92, height = 92, usePhysicalSize = true, className, style } = props;
  const buttonView = (
    <OrientedButtonFrontView
      color={buttonBaseProps?.color ?? "white"}
      horizontal={buttonBaseProps?.horizontal ?? false}
      hasDisplay={buttonBaseProps?.hasDisplay ?? false}
      {...props}
      selected={!renderingPdf && props.selected}
      {...(useFrontImage ? {
        x: 0, y: 0, width, height, usePhysicalSize: false,
        className: "C1_buttonFrontImage", style: { maxWidth: "none", height },
      } : {})}
      uiTemplate={buttonUiTemplate(buttonBaseProps?.type)}
      rows={getC1ButtonRows(eloItem)}
    />
  );
  if (!useFrontImage) return buttonView;
  // Keep catalog photos upright just like the generic button content.
  // Undo reflection before rotation, matching the inverse plan transform.
  const frontImageTransform = [
    frontImageReflected ? `translate(${width} 0) scale(-1 1)` : "",
    props.contentRotation ? `rotate(${-props.contentRotation} ${width / 2} ${height / 2})` : "",
  ].filter(Boolean).join(" ") || undefined;
  return <svg x={x} y={y}
    width={usePhysicalSize ? `${width}mm` : width}
    height={usePhysicalSize ? `${height}mm` : height}
    viewBox={`0 0 ${width} ${height}`} className={className} style={style}>
    {frontImage && <image href={frontImage} width={width} height={height}
      transform={frontImageTransform}
      preserveAspectRatio="none" pointerEvents="none" />}
    {((!standaloneElectrical && !renderingPdf) || !frontImage) && buttonView}
  </svg>;
}
